<script setup lang="ts">
import { DtAvatar } from '@dialpad/dialtone/vue3/lib/avatar';
import { DtSkeleton } from '@dialpad/dialtone/vue3/lib/skeleton';
import { DATA_QA } from './phone-change-log-item.constants';
import type { Phone } from '@/apis/generated/dialbunny';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { computed } from 'vue';
import { useI18N } from '@dialpad/i18n';
import { format } from 'date-fns';
import { useCsvStore, useTargetStore } from '@/stores';
import { getColor } from '@/utils/color-hash';

type Props = {
  prevPhone: Phone | null;
  phone: Phone | null;
  initial?: boolean;
};

const props = defineProps<Props>();

const FIELDS: Partial<Record<keyof Phone, string>> = {
  dnc: 'PHONE_DETAILS_DNC',
  dateDncExpires: 'PHONE_DETAILS_DNC_EXPIRATION',
  dnt: 'PHONE_DETAILS_DNT',
  dateDntExpires: 'PHONE_DETAILS_DNT_EXPIRATION',
};

const changedFields = computed(() => {
  return Object.entries(FIELDS)
    .filter(([key]) => {
      return (
        props.prevPhone?.[key as keyof Phone] !==
        props.phone?.[key as keyof Phone]
      );
    })
    .map(([key]) => key as keyof Phone);
});

const { $t } = useI18N();

const formatValue = (value: boolean | string | number) => {
  if (typeof value === 'boolean') {
    return value
      ? $t('PHONE_DETAILS_BOOLEAN_TRUE')
      : $t('PHONE_DETAILS_BOOLEAN_FALSE');
  }

  if (typeof value === 'number') {
    return format(value, $t('PHONE_DETAILS_DATE_FORMAT'));
  }

  return value;
};

const targetStore = useTargetStore();

const authorId = computed(() => props.phone?.lastAuthorId ?? null);

const author = targetStore.watch(() => authorId.value, 'PhoneChangeLogItem');

const csvStore = useCsvStore();

const csv = csvStore.watch(
  () => props.phone?.lastCsvId ?? null,
  'PhoneChangeLogItem',
);
</script>

<template>
  <div v-if="phone" :data-qa="DATA_QA.ROOT">
    <div class="d-d-flex d-ai-center">
      <DtAvatar
        :name="author.item?.name ?? $t('PHONE_CHANGE_LOG_ITEM_DEFAULT_AUTHOR')"
        :image-alt="
          author.item?.name ?? $t('PHONE_CHANGE_LOG_ITEM_DEFAULT_AUTHOR')
        "
        :image-src="author.item?.imageUrl ?? null"
        :color="
          getColor(
            author.item?.name ?? $t('PHONE_CHANGE_LOG_ITEM_DEFAULT_AUTHOR'),
          )
        "
        class="d-mr12"
      >
        <template v-if="!author.item" #icon="{ iconSize }">
          <DtIcon name="user" :size="iconSize" />
        </template>
      </DtAvatar>

      <div>
        <div :data-qa="DATA_QA.DATE" class="d-fc-tertiary d-fs-100">
          {{
            phone.dateModified
              ? format(
                  phone.dateModified,
                  $t('PHONE_CHANGE_LOG_ITEM_DATE_FORMAT'),
                )
              : '—'
          }}
        </div>
        <i18n
          v-if="!author.loading"
          :path="
            phone.lastCsvId
              ? 'PHONE_CHANGE_LOG_ITEM_IMPORTED'
              : initial
              ? 'PHONE_CHANGE_LOG_ITEM_MANUALLY_CREATED'
              : 'PHONE_CHANGE_LOG_ITEM_UPDATED'
          "
          :data-qa="DATA_QA.MESSAGE"
          tag="div"
          class="d-fc-primary d-fs-200"
        >
          <template #name>
            <span :data-qa="DATA_QA.AUTHOR_NAME">
              {{
                author.item?.name ?? $t('PHONE_CHANGE_LOG_ITEM_DEFAULT_AUTHOR')
              }}
            </span>
          </template>
        </i18n>
        <DtSkeleton
          v-else
          :data-qa-loading="true"
          :text-option="{
            width: '150px',
            type: 'heading',
            headingHeight: 'sm',
          }"
        />
      </div>
    </div>
    <div
      :class="`d-bl d-blw2 d-pl24 d-ml16 d-bc-subtle d-pb24 d-mtn4 ${
        initial ? 'd-bco0' : ''
      }`"
    >
      <div v-if="phone.lastCsvId" class="d-ml2 d-mt8">
        <div class="d-fc-tertiary d-fs-100">
          {{ $t('PHONE_CHANGE_LOG_IMPORT') }}
        </div>
        <div
          v-if="csv.item"
          :data-qa="DATA_QA.CSV_NAME"
          class="d-d-flex d-ai-center"
        >
          <span>{{ csv.item.name }}</span>
        </div>
        <DtSkeleton
          v-else
          :data-qa-loading="true"
          :text-option="{
            width: '150px',
            type: 'heading',
            headingHeight: 'sm',
          }"
        />
      </div>
      <div
        v-if="changedFields.length === 0"
        :data-qa="DATA_QA.CHANGES"
        class="d-fc-tertiary d-fs-100 d-mt8 d-ml2"
      >
        {{ $t('PHONE_CHANGE_LOG_ITEM_NO_CHANGES') }}
      </div>
      <div
        v-for="field in changedFields"
        :key="field"
        :data-qa="DATA_QA.FIELD"
        class="d-ml2 d-mt8"
      >
        <div :data-qa="DATA_QA.FIELD_NAME" class="d-fc-tertiary d-fs-100">
          {{ $t(FIELDS[field] as string) }}
        </div>
        <div class="d-d-flex d-ai-center">
          <span :data-qa="DATA_QA.FIELD_OLD_VALUE">
            {{
              formatValue(
                (props.prevPhone?.[field] ?? '—') as string | number | boolean,
              )
            }}
          </span>
          <DtIcon name="arrow-right" class="d-mx8" size="200" />
          <span :data-qa="DATA_QA.FIELD_NEW_VALUE">
            {{ formatValue(phone[field] as string | number | boolean) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
